import { Color, HeatLegend, IHeatLegendSettings, Tooltip, color, p50, percent } from "@amcharts/amcharts5";
import { MapChart as Am5MapChart, MapPolygonSeries, geoAlbersUsa } from "@amcharts/amcharts5/map";
import am5geodata_usaLow from "@amcharts/amcharts5-geodata/usaLow";
import type { FeatureCollection } from "geojson";

import dmaGeoJson from "@/assets/maps/dma_light_210.json";
import BaseChart from "@/classes/BaseChart";
import { prepareMapDataForExport } from "@/helpers/mapChartExport";
import { MapGeography, mapGeography } from "@/settings/charts/mapGeography";
import { ChartDataRecord } from "@/types/charts";

type PolygonKeyExtractor = (dataContext: Record<string, unknown> | undefined) => string | undefined;

export default class MapChart extends BaseChart {
  private polygonValues: Record<string, number> = {};
  private valueRange: { min: number; max: number } = { min: 0, max: 0 };

  initChart() {
    this.root.container.children.clear();

    const geography = (this.settings.decompositionMetric as MapGeography) ?? mapGeography.states;
    const isStatesMode = geography === mapGeography.states;
    const lowFill = color(this.settings.minValueColor ?? "#4E5068");
    const highFill = color(this.settings.maxValueColor ?? "#DFEBF0");
    const hoverFill = color(this.themeColors["accent-global"] as string);
    const baseStroke = this.root.interfaceColors.get("background") || Color.brighten(highFill, -0.2);

    this.chart = this.root.container.children.push(
      Am5MapChart.new(this.root, {
        width: percent(100),
        height: percent(100),
        panX: "none",
        panY: "none",
        wheelX: "none",
        wheelY: "none",
        projection: geoAlbersUsa(),
        layout: this.layout,
        ...this.getLegendPadding(),
      })
    );

    const statesSeries = (this.chart as Am5MapChart).series.push(
      MapPolygonSeries.new(this.root, {
        geoJSON: am5geodata_usaLow,
      })
    );

    statesSeries.mapPolygons.template.setAll({
      interactive: isStatesMode,
      fill: lowFill,
      stroke: isStatesMode ? baseStroke : lowFill,
      strokeWidth: 1,
      strokeOpacity: isStatesMode ? 1 : 0,
    });

    if (isStatesMode) {
      this.applyHover(statesSeries, hoverFill);
      this.applyHeatFill(statesSeries, lowFill, highFill, (ctx) => ctx?.name as string | undefined);
      this.applyTooltip(
        statesSeries,
        (ctx) => ctx?.name as string | undefined,
        (ctx) => ctx?.name as string | undefined,
        "{name}"
      );
    }

    if (!isStatesMode) {
      const dmaSeries = (this.chart as Am5MapChart).series.push(
        MapPolygonSeries.new(this.root, {
          geoJSON: dmaGeoJson as FeatureCollection,
        })
      );

      dmaSeries.mapPolygons.template.setAll({
        interactive: true,
        fill: lowFill,
        stroke: baseStroke,
        strokeWidth: 0.75,
        strokeOpacity: 0.9,
      });

      this.applyHover(dmaSeries, hoverFill);
      this.applyHeatFill(dmaSeries, lowFill, highFill, (ctx) => ctx?.displayName as string | undefined);
      this.applyTooltip(
        dmaSeries,
        (ctx) => ctx?.displayName as string | undefined,
        (ctx) => ctx?.displayName as string | undefined,
        "{displayName}"
      );
    }

    this.createLegend(lowFill, highFill);
  }

  private applyHover(series: MapPolygonSeries, hoverFill: Color): void {
    series.mapPolygons.template.states.create("hover", {
      stroke: hoverFill,
      strokeWidth: 2,
      strokeOpacity: 1,
    });

    series.mapPolygons.template.events.on("pointerover", (event) => {
      event.target.toFront();
    });
  }

  private applyHeatFill(
    series: MapPolygonSeries,
    lowFill: Color,
    highFill: Color,
    polygonKey: PolygonKeyExtractor
  ): void {
    series.mapPolygons.template.adapters.add("fill", (currentFill, target) => {
      const key = polygonKey(target.dataItem?.dataContext as Record<string, unknown> | undefined);
      const ratio = this.getFillRatio(key);

      return Color.interpolate(ratio, lowFill, highFill, "hsl");
    });
  }

  private getLegendPadding(): Record<string, number> {
    switch (this.settings.legendPosition) {
      case "top":
        return { paddingTop: 56 };
      case "bottom":
        return { paddingBottom: 56 };
      case "left":
        return { paddingLeft: 96 };
      case "right":
        return { paddingRight: 96 };
      default:
        return {};
    }
  }

  private createLegend(lowFill: Color, highFill: Color): void {
    if (!this.settings.legendPosition) {
      return;
    }

    const horizontalSetting = {
      width: percent(40),
      centerX: percent(0),
      x: percent(0),
      marginLeft: 20,
      layout: this.root.gridLayout,
      orientation: "horizontal" as const,
    };
    const verticalSetting = {
      centerX: percent(0),
      x: percent(0),
      centerY: p50,
      y: percent(50),
      layout: this.root.verticalLayout,
      orientation: "vertical" as const,
    };

    let legendSetting;
    switch (this.settings.legendPosition) {
      case "left":
        legendSetting = { ...verticalSetting, marginLeft: 20 };
        break;
      case "right":
        legendSetting = { ...verticalSetting, centerX: percent(100), x: percent(100), marginRight: 20 };
        break;
      case "top":
        legendSetting = { ...horizontalSetting, centerY: percent(0), y: percent(0), marginTop: 16 };
        break;
      case "bottom":
      default:
        legendSetting = { ...horizontalSetting, centerY: percent(100), y: percent(100), marginBottom: 16 };
    }

    legendSetting = {
      ...legendSetting,
      startColor: lowFill,
      endColor: highFill,
      startValue: this.valueRange.min,
      endValue: this.valueRange.max,
    };

    legendSetting = {
      ...legendSetting,
      startText: this.formatMetricValue(this.valueRange.min),
      endText: this.formatMetricValue(this.valueRange.max),
    };

    const heatLegend = HeatLegend.new(this.root, legendSetting as IHeatLegendSettings);

    this.root.container.children.push(heatLegend);
  }

  prepareDataForExport = (): Record<string, unknown>[] =>
    prepareMapDataForExport(
      this.responseData,
      (this.settings.decompositionMetric as MapGeography) ?? mapGeography.states,
      this.metricsDictionary
    );

  protected mapData(data: ChartDataRecord[]): ChartDataRecord[] {
    const dimensionField = this.settings.decompositionMetric as string;
    const metricField = this.settings.displayMetric as string;

    const values: Record<string, number> = {};

    for (const row of data) {
      const key = row[dimensionField];
      if (typeof key !== "string") continue;

      const metric = row[metricField];
      const numeric = typeof metric === "number" ? metric : Number(metric);
      if (!Number.isFinite(numeric)) continue;

      values[key] = (values[key] ?? 0) + numeric;
    }

    this.polygonValues = values;

    const numbers = Object.values(values);
    this.valueRange = numbers.length
      ? { min: Math.min(0, ...numbers), max: Math.max(0, ...numbers) }
      : { min: 0, max: 0 };

    return data;
  }

  private applyTooltip(
    series: MapPolygonSeries,
    polygonKey: PolygonKeyExtractor,
    polygonLabel: PolygonKeyExtractor,
    baseField: string
  ): void {
    series.set("tooltip", Tooltip.new(this.root, {}));
    series.mapPolygons.template.set("tooltipText", baseField);
    series.mapPolygons.template.adapters.add("tooltipText", (text, target) => {
      const ctx = target.dataItem?.dataContext as Record<string, unknown> | undefined;
      const label = polygonLabel(ctx);
      if (!label) return text;

      const value = this.getPolygonValue(polygonKey(ctx));

      return `[bold]${label}[/]\n${this.getMetricTitle()}: ${this.formatMetricValue(value)}`;
    });
  }

  private getMetricTitle(): string {
    const dictionary = this.inputsDictionary["displayMetrics"];
    const metric = Array.isArray(dictionary)
      ? dictionary.find((item) => item.value === this.settings.displayMetric)
      : undefined;

    return metric?.title ?? String(this.settings.displayMetric);
  }

  private formatMetricValue(value: number): string {
    const formatter = this.formatters[this.settings.displayMetric];
    if (typeof formatter !== "function") {
      return String(value);
    }

    return String(this.settings.compactFormat ? formatter(value, { notation: "compact" }) : formatter(value));
  }

  private getPolygonValue(key: string | undefined): number {
    if (!key) return 0;
    return this.polygonValues[key] ?? 0;
  }

  private getFillRatio(key: string | undefined): number {
    const { min, max } = this.valueRange;
    if (max === min) return 0;

    const ratio = (this.getPolygonValue(key) - min) / (max - min);
    return Math.min(1, Math.max(0, ratio));
  }
}
